import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import './Chassis.css';

export interface ChassisProps {
  children: ComponentChildren;
  /** Pointer-tracked matte bloom (a desktop pleasure — settles to a fixed
   *  top light on touch / reduced-motion). On by default; the dial itself
   *  is the pack's --chassis-sheen token, not a prop. */
  sheen?: boolean;
  class?: string;
}

// The one surface a pack may dress (decision 6a8d3df0). Carbon may carry a
// headline; it may never carry a table — data lives on the lit display face,
// which this component deliberately does not provide. Compose a display
// surface INSIDE it.
export function Chassis({ children, sheen = true, class: cls }: ChassisProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !sheen) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const move = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
      el.style.setProperty('--my', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
    };
    const leave = () => {
      el.style.setProperty('--mx', '50%');
      el.style.setProperty('--my', '0%');
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
    };
  }, [sheen]);

  return (
    <div ref={ref} class={`vw-chassis${cls ? ` ${cls}` : ''}`}>
      {sheen ? <div class="vw-chassis__sheen" aria-hidden="true" /> : null}
      <div class="vw-chassis__inner">{children}</div>
    </div>
  );
}
